import axios from "axios";
import { v3Url } from "@/services/api/apiPrefix";

export default {
  namespaced: true,
  state: {
    response: null, // Vai armazenar o cliente (dados do customer)
    customerData: null, // Dados do customer do localStorage
    freightCost: 0, // Custo do frete
    freightLoading: false, // Estado de carregamento do frete
    error: null,
    loading: false,
  },
  mutations: {
    setCustomer(state, customer) {
      state.response = customer; // Atualiza o estado com os dados do cliente
    },
    setCustomerData(state, customerData) {
      state.customerData = customerData; // Atualiza os dados do customer do localStorage
    },
    setFreightCost(state, cost) {
      state.freightCost = cost; // Atualiza o custo do frete
    },
    setFreightLoading(state, loading) {
      state.freightLoading = loading; // Atualiza o estado de carregamento do frete
    },
    setError(state, error) {
      state.error = error; // Atualiza o estado de erro
    },
    setLoading(state, loading) {
      state.loading = loading; // Atualiza o estado de carregamento
    },
  },
  actions: {
    async fetchCustomerByPhoneNumber({ commit, rootState }, phoneNumber) {
      commit("setLoading", true);
      try {
        // Obtém o token do Vuex (certifique-se de que o token está no módulo 'auth')
        const token = rootState.auth.token; // Acessando diretamente o estado do módulo 'auth'

        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }

        // Realiza a requisição para buscar o cliente
        const response = await axios.get(
          v3Url(`/customer/find-by-phone/${phoneNumber}`),
          {
            headers: {
              "Content-Type": "application/json",
              AuthorizationIntegrator: `${token}`, // Cabeçalho correto para autenticação (verifique se o "Bearer" é necessário)
            },
          },
        );

        // Verifica se a resposta é válida e atribui ao estado
        if (response.data && response.data.success) {
          // A resposta da API contém os dados do cliente dentro de `response.response.customer`
          commit("setCustomer", response.data.response.customer); // Salva os dados do cliente no Vuex
        } else {
          commit("setError", "Não foi possível encontrar o cliente");
        }
      } catch (error) {
        // Tratamento de erro mais robusto
        if (error.response) {
          // Se a resposta do servidor estiver disponível
          commit(
            "setError",
            error.response.data.message || "Erro ao buscar o Cliente",
          );
        } else if (error.request) {
          // Se a requisição foi feita, mas não houve resposta
          commit("setError", "Erro de conexão com o servidor");
        } else {
          // Erro desconhecido
          commit("setError", error.message || "Erro desconhecido");
        }
      } finally {
        commit("setLoading", false); // Finaliza a operação de carregamento
      }
    },

    // Action para observar mudanças no localStorage
    watchCustomerData({ commit, dispatch }) {
      const updateCustomerData = () => {
        try {
          const customerDataString = localStorage.getItem("customerData");
          if (customerDataString) {
            const customerData = JSON.parse(customerDataString);
            commit("setCustomerData", customerData);

            // Verifica se tem endereços e postalCode para calcular frete
            if (customerData.addresses && customerData.addresses.length > 0) {
              const address = customerData.addresses[0];
              if (address.postalCode) {
                dispatch("calculateFreight", address.postalCode);
              }
            }
          }
        } catch (error) {
          console.error(
            "Erro ao processar customerData do localStorage:",
            error,
          );
        }
      };

      // Atualiza imediatamente
      updateCustomerData();

      // Observa mudanças no localStorage
      const originalSetItem = localStorage.setItem;
      localStorage.setItem = function (key, value) {
        originalSetItem.apply(this, arguments);
        if (key === "customerData") {
          updateCustomerData();
        }
      };

      // Observa eventos de storage (mudanças de outras abas)
      window.addEventListener("storage", (event) => {
        if (event.key === "customerData") {
          updateCustomerData();
        }
      });
    },

    // Action para calcular o frete
    async calculateFreight({ commit, rootState }, postalCode) {
      if (!postalCode) return;

      commit("setFreightLoading", true);
      try {
        const token = rootState.auth.token || localStorage.getItem("authToken");

        // Buscar o telefone do point of sale
        const pointOfSaleId =
          rootState.product?.pointOfSaleId || localStorage.getItem("pdvId");
        if (!pointOfSaleId) {
          throw new Error("Point of Sale ID não encontrado");
        }

        // Primeiro, buscar dados do point of sale para obter o telefone
        const designSystemResponse = await axios.get(
          v3Url(`/business/design-system-config/${pointOfSaleId}`),
          {
            headers: {
              "Content-Type": "application/json",
              AuthorizationIntegrator: `${token}`,
            },
          },
        );

        if (!designSystemResponse.data.success) {
          throw new Error("Erro ao buscar dados do point of sale");
        }

        const phonePointOfSale =
          designSystemResponse.data.response.pointOfSale.phone;
        if (!phonePointOfSale) {
          throw new Error("Telefone do point of sale não encontrado");
        }

        // Agora calcular o frete
        const freightResponse = await axios.get(
          v3Url(`/freight/cost/${postalCode}/${phonePointOfSale}`),
          {
            headers: {
              "Content-Type": "application/json",
              AuthorizationIntegrator: `${token}`,
            },
          },
        );

        if (freightResponse.data && freightResponse.data.success) {
          if (!freightResponse.data.response.eligible) {
            push.error(
              "Sinto muito, mas não é possível realizar a entrega para este endereço",
            );
            throw new Error(
              "Sinto muito, mas não é possível realizar a entrega para este endereço",
            );
          }
          const freightCost = freightResponse.data.response.freightCost;
          commit("setFreightCost", Number(freightCost));
        } else {
          console.warn("Não foi possível calcular o frete");
          commit("setFreightCost", 0);
        }
      } catch (error) {
        console.error("Erro ao calcular frete:", error);
        commit("setFreightCost", 0); // Define como 0 em caso de erro
      } finally {
        commit("setFreightLoading", false);
      }
    },
  },
  getters: {
    getCustomer(state) {
      return state.response; // Retorna os dados do cliente
    },
    getCustomerData(state) {
      return state.customerData; // Retorna os dados do customer do localStorage
    },
    getCustomerAddresses(state) {
      return state.customerData?.addresses || []; // Retorna os endereços do customer
    },
    getPostalCode(state) {
      return state.customerData?.addresses?.[0]?.postalCode || null; // Retorna o postalCode do primeiro endereço
    },
    getFreightCost(state) {
      return state.freightCost; // Retorna o custo do frete
    },
    getFreightLoading(state) {
      return state.freightLoading; // Retorna o estado de carregamento do frete
    },
    getError(state) {
      return state.error;
    },
    getLoading(state) {
      return state.loading;
    },
  },
};
